// @ts-nocheck
import React, { useState, useEffect } from 'react';
import * as MUI from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import {
  IconButton,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Card,
  CardContent,
  Typography,
  Divider,
  Box,
  Paper
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useThemeContext, ColorModeToggleButton } from '../theme/themeContext';
import { signOut as supabaseSignOut, getCurrentUser } from '../supabase';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const Header: React.FC<{
  menuItems: any[];
  onOpenDrawer: () => void;
  handleDrawerClose: () => void;
  open: boolean;
  miniVariant?: boolean;
  toggleMiniVariant?: () => void;
}> = ({ menuItems, onOpenDrawer, handleDrawerClose, open, miniVariant, toggleMiniVariant }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [user, setUser] = useState(null);
  const { mode } = useThemeContext?.() || { mode: 'light' };

  // Fetch current user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUser();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await supabaseSignOut();
      handleMenuClose();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const location = useLocation();

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.email) return '?';

    // Try to get initials from email (first letter before @ symbol)
    const emailName = user.email.split('@')[0];
    if (emailName) {
      return emailName.charAt(0).toUpperCase();
    }

    return '?';
  };

  return (
    <MUI.AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <MUI.Toolbar>
        {!open && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={onOpenDrawer}
            edge="start"
            sx={{ marginRight: '36px' }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {open && !miniVariant && location.pathname !== "/" && (
          <IconButton onClick={handleDrawerClose} style={{ color: '#fff' }}>
            <ChevronLeftIcon />
          </IconButton>
        )}

        {open && miniVariant && location.pathname !== "/" && toggleMiniVariant && (
          <IconButton onClick={toggleMiniVariant} style={{ color: '#fff' }}>
            <ChevronRightIcon />
          </IconButton>
        )}

        {/* Custom Title */}
        <MUI.Typography variant="h6" component={Link} to="/Dashboard" style={{ flexGrow: 1, textDecoration: 'none', color: 'inherit' }}>
          <div className="navbar-title" style={{ fontWeight: 'bold' }}>
            <span>GLS</span>
            <span>.</span>
            <span>UPTIME TRACKER</span>
          </div>
        </MUI.Typography>

        {/* Other Toolbar content */}
        <Avatar
          onClick={handleMenuOpen}
          style={{ cursor: 'pointer', backgroundColor: user ? undefined : 'gray' }}
          sx={{
            width: 40,
            height: 40,
            mr: 1,
            '&:hover': {
              opacity: 0.8,
              boxShadow: '0 0 5px rgba(255,255,255,0.5)'
            }
          }}
        >
          {user?.user_metadata?.avatar_url ? (
            <img
              src={user.user_metadata.avatar_url}
              alt="User"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            getUserInitials()
          )}
        </Avatar>

        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={isMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              minWidth: 250,
              borderRadius: 2,
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
        >
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar
                sx={{
                  width: 50,
                  height: 50,
                  mr: 2,
                  bgcolor: user ? 'primary.main' : 'gray'
                }}
              >
                {user?.user_metadata?.avatar_url ? (
                  <img
                    src={user.user_metadata.avatar_url}
                    alt="User"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <PersonIcon />
                )}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {user?.user_metadata?.full_name || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {user?.email || 'Loading...'}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <MenuItem
              onClick={handleLogout}
              sx={{
                borderRadius: 1,
                py: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
            >
              <LogoutIcon sx={{ mr: 2, color: 'error.main' }} />
              <Typography color="error.main">Logout</Typography>
            </MenuItem>
          </Box>
        </Menu>
        <ColorModeToggleButton />
      </MUI.Toolbar>
    </MUI.AppBar>
  );
};

export default Header;